import { Link } from 'react-router-dom';
import { pageMeta } from '../../constants/pageMeta';
import { COMPANY_EMAIL, COMPANY_NAME, SITE_DOMAIN, SITE_NAME } from '../../constants/site';
import PolicyPage, { ContactBlock, List, Section } from '../components/PolicyPage';

const META = pageMeta(
  'Privacy Policy',
  `How ${COMPANY_NAME} (${SITE_NAME}) collects, uses, shares and protects your personal information.`,
  '/privacy-policy'
);

export default function PrivacyPolicyPage() {
  return (
    <PolicyPage
      title="Privacy Policy"
      meta={META}
      intro={`This policy explains what personal information ${COMPANY_NAME} (“${SITE_NAME}”, “we”, “us”) collects when you use ${SITE_DOMAIN} or contact us, how we use it, and the choices you have.`}
    >
      <Section title="1. Who is responsible for your information">
        <p>
          {COMPANY_NAME}, the operator of {SITE_NAME}, is responsible for the personal information described in this
          policy. Contact details are at the end of this page.
        </p>
      </Section>

      <Section title="2. Information we collect">
        <List
          items={[
            <><strong>Information you give us:</strong> your name, email address, phone number, travel plans and preferences when you request a quote, call or email us.</>,
            <><strong>Booking information:</strong> traveller names as shown on ID, dates of birth, passport or loyalty numbers, special requests and billing details needed to complete a booking you approve.</>,
            <><strong>Payment information:</strong> handled by our payment processors or the travel provider. We do not store full card numbers on our systems.</>,
            <><strong>Call information:</strong> the date, time and length of calls, and call recordings where you are told at the start of the call that it may be recorded.</>,
            <><strong>Information collected automatically:</strong> IP address, browser and device type, pages visited, referring page and the advertisement you clicked, collected through cookies and similar technologies (see our <Link to="/cookie-policy">Cookie Policy</Link>).</>,
          ]}
        />
      </Section>

      <Section title="3. How we use your information">
        <List
          items={[
            'To prepare quotes, make bookings you approve and send confirmations.',
            'To help with changes, cancellations, refunds and other requests about your trip.',
            'To answer your questions and keep records of our communications.',
            'To prevent fraud and keep our website and systems secure.',
            'To understand how our website and advertising perform, and to improve them.',
            'To send you marketing emails only if you have agreed; you can unsubscribe at any time.',
            'To meet our legal, tax and accounting obligations.',
          ]}
        />
      </Section>

      <Section title="4. Who we share it with">
        <p>We share personal information only where needed for the purposes above:</p>
        <List
          items={[
            'Travel providers (such as airlines, hotels, car rental companies and cruise lines) to make and manage your booking. Their own privacy policies then apply.',
            'Payment processors, to take payments you authorise.',
            'Service providers that host our website, provide phone and email systems, or help with analytics and advertising, under contracts that limit their use of the data.',
            'Authorities, where required by law — for example passenger data that airlines must share with border agencies.',
            'A buyer or successor if our business is sold or reorganised.',
          ]}
        />
        <p>
          <strong>We do not sell your personal information.</strong>
        </p>
      </Section>

      <Section title="5. Advertising and analytics">
        <p>
          We use advertising services, such as Google Ads, to show ads for {SITE_NAME} and to measure how many people
          visit our site or call us after seeing an ad. These services may use cookies or similar identifiers to
          measure conversions and, where permitted, to show you our ads on other websites. You can control
          personalised advertising at{' '}
          <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
            Google Ad Settings
          </a>{' '}
          and learn how Google uses information at{' '}
          <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">
            How Google uses information from sites that use its services
          </a>
          . You can also opt out of many interest-based ads at{' '}
          <a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer">
            optout.aboutads.info
          </a>
          .
        </p>
      </Section>

      <Section title="6. How long we keep it">
        <p>
          We keep booking and payment records for as long as tax and accounting laws require (usually up to seven
          years). Quote requests that do not lead to a booking are deleted within two years. Call recordings are kept
          for up to 12 months unless needed for a dispute.
        </p>
      </Section>

      <Section title="7. Security">
        <p>
          We use reasonable technical and organisational measures, including encrypted (HTTPS) connections and
          restricted staff access, to protect your information. No method of transmission or storage is completely
          secure, so please never send full card numbers by email.
        </p>
      </Section>

      <Section title="8. Your rights and choices">
        <p>Depending on where you live, you may have the right to:</p>
        <List
          items={[
            'Ask for a copy of the personal information we hold about you.',
            'Ask us to correct or delete it.',
            'Object to or limit how we use it, including for marketing.',
            'Withdraw consent you have given at any time.',
            'Complain to your local data protection authority.',
          ]}
        />
        <p>
          <strong>California residents:</strong> under the CCPA/CPRA you may request to know, delete or correct your
          personal information and to opt out of the “sale” or “sharing” of it. We do not sell personal information.
          To opt out of sharing for cross-context behavioural advertising, email us or use the browser controls in our{' '}
          <Link to="/cookie-policy">Cookie Policy</Link>. We will not treat you differently for using your rights.
        </p>
        <p>
          To make any request, email <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>. We may need to verify your
          identity and will respond within the time required by law.
        </p>
      </Section>

      <Section title="9. Children">
        <p>
          Our website is not directed at children under 16, and we do not knowingly collect their information except
          as provided by a parent or guardian to book travel for them.
        </p>
      </Section>

      <Section title="10. International transfers">
        <p>
          We are based in the United States. If you contact us from elsewhere, your information will be processed in
          the United States and in the countries of the travel providers involved in your trip.
        </p>
      </Section>

      <Section title="11. Changes to this policy">
        <p>We may update this policy from time to time. The date at the top shows when it last changed.</p>
      </Section>

      <Section title="12. Contact us">
        <ContactBlock />
      </Section>
    </PolicyPage>
  );
}
